import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Clock3 } from 'lucide-react';
import { fetchWatchtimeLeaderboard, LeaderboardEntry, LeaderboardResponse } from '../api';
import { fetchUserProfilePic, getDefaultAvatarUrl } from '../utils/profilePicCache';
import { useDebounce } from '../hooks/use-debounce';
import LoadingIndicator from './LoadingIndicator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './animate-ui/components/radix/dialog';

export function formatWatchtime(value?: number) {
  const totalHours = Math.floor(Number(value || 0) / 1_000_000_000 / 60 / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;

  if (days > 0 && hours > 0) return `${days} nap ${hours} óra`;
  if (days > 0) return `${days} nap`;
  return `${hours} óra`;
}

function WatchtimeRow({
  entry,
  index,
  profilePic
}: {
  entry: LeaderboardEntry;
  index: number;
  profilePic?: string;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-2.5 rounded-[3rem] bg-white/5 px-2.5 py-2 backdrop-blur-sm transition-colors duration-200 hover:bg-white/10 md:gap-3 md:px-3 ${index === 0 ? 'ring-1 ring-white/20' : ''}`}
      style={{
        boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.1), 0 4px 6px rgba(0, 0, 0, 0.1)'
      }}
    >
      <div className="flex min-w-0 items-center gap-2.5 md:gap-3">
        <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full bg-white/10 ring-1 ring-white/10 md:h-10 md:w-10">
          <img
            src={profilePic || getDefaultAvatarUrl(entry.name)}
            alt={entry.name}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
            onError={(event) => {
              event.currentTarget.src = getDefaultAvatarUrl(entry.name);
            }}
          />
        </div>
        <div className="min-w-0">
          <div className="truncate font-medium text-white/90">{entry.name}</div>
          <div className="text-xs text-white/50">#{entry.rank || index + 1}</div>
        </div>
      </div>
      <span className="shrink-0 text-xs font-semibold tabular-nums text-white/80 md:text-sm">
        {formatWatchtime(entry.watchtime)}
      </span>
    </div>
  );
}

const emptyWatchtimeResponse: LeaderboardResponse = {
  data: [],
  page: 1,
  pageSize: 50,
  totalEntries: 0,
  totalPages: 0
};

const WatchtimeLeaderboardCard: React.FC = () => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [modalData, setModalData] = useState<LeaderboardResponse>(emptyWatchtimeResponse);
  const [profilePics, setProfilePics] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [shouldAnimateModalList, setShouldAnimateModalList] = useState(false);
  const shouldAnimateModalListRef = React.useRef(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const updateModalListAnimation = React.useCallback((value: boolean) => {
    shouldAnimateModalListRef.current = value;
    setShouldAnimateModalList(value);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadTopFive() {
      setLoading(true);
      const data = await fetchWatchtimeLeaderboard(1);
      if (!cancelled) {
        setEntries(data.data.slice(0, 5));
        setLoading(false);
      }
    }

    loadTopFive();
    const timer = window.setInterval(loadTopFive, 10 * 60 * 1000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    async function loadModalData() {
      setIsSearching(true);
      const data = await fetchWatchtimeLeaderboard(currentPage, debouncedSearchTerm);

      if (!cancelled) {
        setModalData(data);
        setIsSearching(false);
        if (shouldAnimateModalListRef.current) {
          window.setTimeout(() => updateModalListAnimation(false), 420);
        }
      }
    }

    loadModalData();

    return () => {
      cancelled = true;
    };
  }, [currentPage, debouncedSearchTerm, isOpen, updateModalListAnimation]);

  useEffect(() => {
    let cancelled = false;

    async function fetchProfilePics() {
      const names = Array.from(new Set([...entries, ...modalData.data].map(entry => entry.name).filter(Boolean)));

      for (const name of names) {
        if (profilePics[name]) continue;

        try {
          const url = await fetchUserProfilePic(name);
          if (!cancelled) {
            setProfilePics(prev => ({ ...prev, [name]: url }));
          }
        } catch (error) {
          console.error(`Failed to fetch profile pic for ${name}:`, error);
        }
      }
    }

    fetchProfilePics();

    return () => {
      cancelled = true;
    };
  }, [entries, modalData.data, profilePics]);

  function handleOpenChange(open: boolean) {
    setIsOpen(open);
    if (open) {
      updateModalListAnimation(true);
    }
    if (!open) {
      setSearchTerm('');
      setCurrentPage(1);
      updateModalListAnimation(false);
    }
  }

  return (
    <>
      <div className="glass-card p-4 md:p-6">
        <div className="mb-4 flex items-center justify-between gap-3 md:mb-6">
          <h2 className="flex min-w-0 items-center gap-2 text-lg font-semibold md:text-xl">
            <Clock3 className="text-white/70" size={22} />
            Watchtime
          </h2>
          <button
            type="button"
            onClick={() => handleOpenChange(true)}
            className="h-8 rounded-full bg-white/10 px-3 text-xs text-white/90 transition hover:bg-white/20 active:scale-[0.98] md:text-sm"
          >
            Megnyitás
          </button>
        </div>

        {loading ? (
          <div className="flex min-h-[190px] items-center justify-center">
            <LoadingIndicator compact />
          </div>
        ) : entries.length ? (
          <div className="space-y-2 md:space-y-3">
            {entries.map((entry, index) => (
              <WatchtimeRow
                key={`${entry.name}-${entry.rank || index}`}
                entry={entry}
                index={index}
                profilePic={profilePics[entry.name]}
              />
            ))}
          </div>
        ) : (
          <div className="flex min-h-[190px] items-center justify-center rounded-lg border border-white/10 bg-white/5 text-sm text-white/45">
            Nincs watchtime adat
          </div>
        )}
      </div>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="flex h-[86dvh] max-w-[calc(100%-0.75rem)] flex-col gap-3 rounded-2xl border border-white/10 bg-black/95 p-3 md:h-[80vh] md:max-w-2xl md:gap-4 md:p-6">
          <DialogHeader>
            <DialogTitle>Watchtime Leaderboard</DialogTitle>
          </DialogHeader>

          <div className="relative">
            <input
              type="text"
              aria-label="Watchtime leaderboard keresés"
              placeholder="Keresés név alapján..."
              value={searchTerm}
              onChange={(event) => {
                updateModalListAnimation(false);
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-white transition placeholder:text-white/40 focus:border-white/30 focus:outline-none"
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <LoadingIndicator compact />
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto pr-2">
            {modalData.data.length ? (
              <motion.div
                initial={shouldAnimateModalList ? { opacity: 0, filter: 'blur(10px)', clipPath: 'inset(0 0 14% 0)' } : false}
                animate={{
                  opacity: isSearching ? 0.72 : 1,
                  filter: 'blur(0px)',
                  clipPath: 'inset(0 0 0% 0)'
                }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                className="space-y-2"
                style={{
                  WebkitFontSmoothing: 'subpixel-antialiased',
                  MozOsxFontSmoothing: 'auto',
                  transform: 'translateZ(0)',
                  backfaceVisibility: 'hidden',
                  perspective: 1000
                }}
              >
                {modalData.data.map((entry, index) => (
                  <motion.div
                    key={`${entry.name}-${entry.rank || index}`}
                    initial={shouldAnimateModalList ? { opacity: 0, x: -10, scale: 0.985 } : false}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    transition={{
                      duration: 0.28,
                      delay: shouldAnimateModalList ? Math.min(index, 12) * 0.025 : 0,
                      ease: [0.22, 1, 0.36, 1]
                    }}
                  >
                    <WatchtimeRow
                      entry={entry}
                      index={(currentPage - 1) * modalData.pageSize + index}
                      profilePic={profilePics[entry.name]}
                    />
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <p className="py-8 text-center text-white/60">
                {searchTerm ? 'Nincs találat' : 'Nincs watchtime adat'}
              </p>
            )}
          </div>

          {modalData.totalPages > 1 && (
            <div className="flex items-center justify-between gap-2 border-t border-white/10 pt-2">
              <button
                type="button"
                onClick={() => {
                  updateModalListAnimation(false);
                  setCurrentPage(prev => Math.max(1, prev - 1));
                }}
                disabled={currentPage === 1}
                className="rounded-lg bg-white/5 px-3 py-2 text-sm transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30 md:px-4"
              >
                Előző
              </button>

              <div className="text-sm font-semibold tabular-nums text-white/70">
                {currentPage} / {modalData.totalPages}
              </div>

              <button
                type="button"
                onClick={() => {
                  updateModalListAnimation(false);
                  setCurrentPage(prev => Math.min(modalData.totalPages, prev + 1));
                }}
                disabled={currentPage === modalData.totalPages}
                className="rounded-lg bg-white/5 px-3 py-2 text-sm transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30 md:px-4"
              >
                Következő
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default React.memo(WatchtimeLeaderboardCard);
